import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { request } from '../../api/client.js';
import { kioskApi } from '../../api/kiosk.js';
import { ApiError } from '../../api/client.js';
import { Card } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { t } from '../../lib/i18n.js';

const STORAGE_KEY = 'orme:kiosk:session';

function chatWithCustomerSession(message, conversationId, customerSessionId) {
  return request('/ai/chat', {
    method: 'POST',
    body: { message, conversationId },
    headers: { 'X-Customer-Session': customerSessionId }
  });
}

export function KioskChat({ session, onSessionEnded }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const chatMutation = useMutation({
    mutationFn: (text) => chatWithCustomerSession(text, conversationId, session.customerSessionId),
    onSuccess: (data) => {
      if (data.conversationId && data.conversationId !== conversationId) {
        setConversationId(data.conversationId);
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply || (data.message && data.message.content) || '' }]);
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.statusCode === 451 || err.code === 'FORBIDDEN')) {
        toast.error('Consenso scaduto. Per favore riavvia la conversazione.');
        try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
        onSessionEnded();
        return;
      }
      toast.error(err instanceof ApiError ? err.message : t('common.error'));
      setMessages((prev) => prev.slice(0, -1));
    }
  });

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setDraft('');
    chatMutation.mutate(text);
  };

  const endSession = useMutation({
    mutationFn: () => kioskApi.revoke(session.customerSessionId, 'customer_ended'),
    onSettled: () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
      onSessionEnded();
    }
  });

  const onKey = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col px-6 py-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          <span>Sessione anonima attiva</span>
          {(session.purposes || []).map((p) => <Badge key={p} tone="success">{p}</Badge>)}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setConfirmEnd(true)}>Termina</Button>
      </div>

      <Card className="flex flex-1 flex-col p-0">
        <div ref={listRef} className="flex-1 overflow-y-auto p-5">
          {messages.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <div className="max-w-md text-center">
                <p className="text-base text-ink">Ciao 👋</p>
                <p className="mt-2 text-sm text-ink-muted">
                  Sono l'assistente del negozio. Dimmi che tipo di animale hai (specie, razza, età, peso, eventuali esigenze)
                  e ti consiglierò i prodotti più adatti.
                </p>
              </div>
            </div>
          ) : (
            <ul className="flex flex-col gap-4">
              {messages.map((m, idx) => (
                <li key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-base ${m.role === 'user' ? 'bg-ink text-white' : 'bg-surface text-ink border border-border'}`}>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  </div>
                </li>
              ))}
              {chatMutation.isPending && (
                <li className="flex justify-start">
                  <div className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink-muted">
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-ink-muted" /> sta scrivendo...
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>
        <div className="border-t border-border p-3">
          <div className="flex gap-2">
            <textarea
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              placeholder="Scrivi un messaggio... (Invio per inviare con Cmd/Ctrl)"
              className="flex-1 rounded-md border border-border bg-surface-elevated px-3 py-2 text-base focus:outline-none"
            />
            <Button onClick={send} loading={chatMutation.isPending} disabled={!draft.trim()} size="lg">
              Invia
            </Button>
          </div>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmEnd}
        title="Terminare la conversazione?"
        description="Tutti i dati di questa sessione anonima saranno cancellati."
        destructive
        confirmLabel="Termina"
        loading={endSession.isPending}
        onCancel={() => setConfirmEnd(false)}
        onConfirm={() => endSession.mutate()}
      />
    </div>
  );
}
